import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tiersControllerCreateV1,
  tiersControllerFindAllV1,
  tiersControllerRemoveV1,
  tiersControllerUpdateV1,
} from '@/api/generated/tiers/tiers';
import type {
  CreateTierDto,
  TiersControllerFindAllV1Params,
  UpdateTierDto,
} from '@/api/generated/model';

export const tierKeys = {
  all: ['tiers'] as const,
  lists: () => [...tierKeys.all, 'list'] as const,
  list: (params: TiersControllerFindAllV1Params) => [...tierKeys.lists(), params] as const,
};

export function useTiersQuery(params: TiersControllerFindAllV1Params) {
  return useQuery({
    queryKey: tierKeys.list(params),
    queryFn: () => tiersControllerFindAllV1(params),
    placeholderData: (previous) => previous,
  });
}

/** Every non-archived tier, for pickers. The catalog is a handful of rows. */
export function useAssignableTiersQuery() {
  return useQuery({
    queryKey: tierKeys.list({ limit: 100 }),
    queryFn: () => tiersControllerFindAllV1({ limit: 100 }),
    select: (page) => page.data,
  });
}

function useInvalidateTiers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: tierKeys.all });
}

export function useCreateTierMutation() {
  const invalidate = useInvalidateTiers();
  return useMutation({
    mutationFn: (dto: CreateTierDto) => tiersControllerCreateV1(dto),
    onSuccess: invalidate,
  });
}

export function useUpdateTierMutation() {
  const invalidate = useInvalidateTiers();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTierDto }) =>
      tiersControllerUpdateV1(id, dto),
    onSuccess: invalidate,
  });
}

export function useRemoveTierMutation() {
  const invalidate = useInvalidateTiers();
  return useMutation({
    mutationFn: (id: string) => tiersControllerRemoveV1(id),
    onSuccess: invalidate,
  });
}
